import React, { memo, useMemo, FC } from 'react';
import clsx from 'clsx';
import { Button, RichText } from '@marriott/mi-ui-library';
import { CardPointsList } from '../CardPointsList';
import { CardDetailsContentProps, ReferAFriendLinkProps, RafTrackingProperties } from './CardDetailsContent.types';
import { StyledCardDetailsContent } from './CardDetailsContent.styles';
import { LinkType, LinkTarget, StringBoolean, ARIA_LABELS } from '../../modules/constants';

/**
 * Builds tracking properties object from RAF tracking props
 * Memoized to prevent object recreation
 */
export const useRafTrackingProperties = (referTrackingProperties?: RafTrackingProperties) =>
  useMemo(() => {
    if (!referTrackingProperties) return null;

    return {
      trackingContentPosition: referTrackingProperties.raftrackingContentPosition,
      trackingDescription: referTrackingProperties.raftrackingDescription,
      trackingOfferType: referTrackingProperties.raftrackingOfferType,
      trackingTag: referTrackingProperties.raftrackingTag,
      merchandisingCategory: referTrackingProperties.rafmerchandisingCategory,
      atCCeVar48: referTrackingProperties.rafatCCeVar48,
      additionalTrackingVariables: referTrackingProperties.rafadditionalTrackingVariables,
      clickTrack: referTrackingProperties.rafclickTrack === StringBoolean.TRUE,
      impressionTrack: referTrackingProperties.rafimpressionTrack === StringBoolean.TRUE,
      impressionCount: referTrackingProperties.rafimpressionCount === StringBoolean.TRUE,
      isCoBrand: true,
    };
  }, [referTrackingProperties]);

/**
 * CardDetailsContent - Molecule component showing card points and benefits
 *
 * Performance optimizations:
 * - Memoized with React.memo
 * - Custom hook for tracking properties
 * - Extracted sub-components for better code organization
 * - Early return pattern for better readability
 */
export const CardDetailsContent: FC<CardDetailsContentProps> = memo<CardDetailsContentProps>(props => {
  const {
    componentId,
    cardDescription,
    pointsList,
    cardType,
    pointsHeader,
    showAccordion = false,
    referCta,
    referTrackingProperties,
  } = props;

  // Memoize tracking properties
  const raftrackingProperties = useRafTrackingProperties(referTrackingProperties);

  // Memoize computed values
  const raflinkType = useMemo<LinkType>(
    () => (referCta?.openReferLinkInNewTab === StringBoolean.TRUE ? LinkType.EXTERNAL : LinkType.INTERNAL),
    [referCta?.openReferLinkInNewTab]
  );

  const rafclickTrackValue = useMemo<string>(() => {
    if (!referTrackingProperties) return '';
    return `${referTrackingProperties.raftrackingContentPosition}|${referTrackingProperties.raftrackingDescription}|${raflinkType}|${referTrackingProperties.rafadditionalTrackingVariables}`;
  }, [referTrackingProperties, raflinkType]);

  // Early return if no content to display
  const hasContent = pointsList?.length > 0 || cardDescription || referCta;
  if (!hasContent) {
    return null;
  }

  return (
    <StyledCardDetailsContent
      data-component-name="m-cobrand-CardDetailsContent"
      data-testid="cobrand-CardDetailsContent"
      className={clsx('col-xl-8 col-lg-7 col-md-8 col-12 mb-lg-2 pr-md-3 pr-lg-5 mr-lg-2 px-0 flex-column', {
        'd-flex align-items-center': showAccordion,
        'd-none d-md-block': !showAccordion,
      })}
    >
      {/* Points Earning Section */}
      {pointsList && pointsList.length > 0 && (
        <CardPointsList
          pointsHeader={pointsHeader}
          pointsList={pointsList}
          cardType={cardType}
          componentId={componentId}
        />
      )}

      {/* Card Description / Benefits */}

      {/* TODO ask AEM team to author the content in cardDescription instead of pointsHeader section */}

      {cardDescription && (
        <RichText text={cardDescription} componentId={`accordion-${componentId}`} customClass="mt-2 mt-md-4 mt-lg-3" />
      )}

      {/* Refer a Friend Link - Hidden by default, shown via JS */}
      {referCta && (
        <ReferAFriendLink
          referCta={referCta}
          raflinkType={raflinkType}
          raftrackingProperties={raftrackingProperties}
          rafclickTrackValue={rafclickTrackValue}
        />
      )}
    </StyledCardDetailsContent>
  );
});

CardDetailsContent.displayName = 'CardDetailsContent';

/**
 * ReferAFriendLink - Separated refer-a-friend CTA component
 */
const ReferAFriendLink: FC<ReferAFriendLinkProps> = memo(
  ({ referCta, raflinkType, raftrackingProperties, rafclickTrackValue }) => (
    <div
      className="refer-friend-link col-12 p-0 text-align-left d-none mt-lg-2"
      data-creditcardcode={referCta.creditCardCode}
    >
      <Button
        isLink
        href={referCta.referLinkUrl}
        target={referCta.openReferLinkInNewTab === StringBoolean.TRUE ? LinkTarget.BLANK : LinkTarget.SELF}
        className="m-link pb-1 mb-1 mt-1 mt-3 mr-0 track-completed"
        linkType={raflinkType}
        buttonCopy={referCta.referLinkText}
        trackingProperties={raftrackingProperties || undefined}
        custom_click_track_value={rafclickTrackValue}
        linkAriaLabelOpenNewWindow={
          referCta.openReferLinkInNewTab === StringBoolean.TRUE ? ARIA_LABELS.OPEN_NEW_TAB : ''
        }
      />
    </div>
  )
);

ReferAFriendLink.displayName = 'ReferAFriendLink';
